// Agent access control, test-file mode only: the request header in which
// the viewer names the test user. Here (zod-free leaf) so the browser and
// the server share one spelling.
export const AGENT_TEST_USER_HEADER = 'x-agent-test-user';

// Read tools: no human gate; the browser fulfils them automatically.
export const LIST_STRUCTURES_TOOL_NAME = 'list_structures';
export const ANALYZE_IMAGE_FOR_MODEL_SELECTION_TOOL_NAME =
    'analyze_image_for_model_selection';

// PACS patient read tools (through the connector): no human gate; the
// browser fulfils them automatically.
export const SEARCH_PATIENTS_TOOL_NAME = 'search_patients';
export const LIST_PATIENT_STUDIES_TOOL_NAME = 'list_patient_studies';
// Connector-side read tools: the connector's own record of imported
// patients, and its configured PACS targets + connectivity.
export const LIST_IMPORTED_PATIENTS_TOOL_NAME = 'list_imported_patients';
export const CHECK_PACS_CONNECTION_TOOL_NAME = 'check_pacs_connection';
// Loading series into the viewer: gated (Accept/Reject card). Apply only
// opens the DICOM import dialog pre-filled; the clinician confirms the load.
export const PROPOSE_LOAD_SERIES_TOOL_NAME = 'propose_load_series';

// Contour+ job tools. Checking jobs is read-only; cancelling a job and
// sending a structure set to a PACS are gated (Apply/Reject card).
export const CHECK_AUTO_SEGMENTATION_JOBS_TOOL_NAME =
    'check_auto_segmentation_jobs';
export const CANCEL_AUTO_SEGMENTATION_TOOL_NAME = 'cancel_auto_segmentation';
export const SEND_STRUCTURE_SET_TO_PACS_TOOL_NAME =
    'send_structure_set_to_pacs';

// Contour+ model tools: gated like the mutating tools (Accept/Reject card);
// they change the Contour+ model selection / start a job, never a structure.
export const PROPOSE_CONTOUR_MODEL_TOOL_NAME = 'propose_contour_model';
export const START_AUTO_SEGMENTATION_TOOL_NAME = 'start_auto_segmentation';

// Anatomical-region vocabulary shared by the image-analysis tool, the
// /classify-slices route and the client. Lives in this zod-free leaf so the
// browser can import the values at runtime.
export const BODY_REGIONS = [
    'HEAD_NECK',
    'CHEST',
    'ABDOMEN',
    'PELVIS',
] as const;
export type BodyRegion = (typeof BODY_REGIONS)[number];
// Per-slice answers add "not one of the regions" and "can't tell".
export const SLICE_REGION_LABELS = [
    ...BODY_REGIONS,
    'OTHER',
    'UNCERTAIN',
] as const;
export type SliceRegionLabel = (typeof SLICE_REGION_LABELS)[number];
// Modalities the representative-slice classifier accepts (axial stacks).
export const SLICE_CLASSIFICATION_MODALITIES = ['CT', 'MR'] as const;
export type SliceClassificationModality =
    (typeof SLICE_CLASSIFICATION_MODALITIES)[number];

// Mutating tools: each gated, client-forwarded, no server execute.
export const APPLY_SYMMETRIC_MARGIN_TOOL_NAME = 'apply_symmetric_margin';
export const CREATE_RING_TOOL_NAME = 'create_ring';
export const APPLY_ASYMMETRIC_MARGIN_TOOL_NAME = 'apply_asymmetric_margin';
export const BOOLEAN_OPERATION_TOOL_NAME = 'boolean_operation';
// Gated tools that edit an existing structure in place, like their toolbar
// buttons (one undo step); refused for approved structures.
export const FILL_CAVITIES_TOOL_NAME = 'fill_cavities';
export const REMOVE_SMALL_PARTS_TOOL_NAME = 'remove_small_parts';
export const CLEAR_CONTOURS_TOOL_NAME = 'clear_contours';
// The toolbar's Remove small parts default (cm³). The clinician chooses the
// threshold; this value is only OFFERED when they give none. Here (zod-free
// leaf) so the browser shows the identical value at runtime.
export const DEFAULT_MIN_PART_VOLUME_CM3 = 0.5;

// View tool: changes only what is on screen (focus/zoom), never contour
// data, so it is not gated; the browser fulfils it automatically.
export const ZOOM_VIEW_TOOL_NAME = 'zoom_view';
// More view-only tools (no card): structure visibility, moving to a
// position, window/level presets.
export const SET_STRUCTURE_VISIBILITY_TOOL_NAME = 'set_structure_visibility';
export const GO_TO_POSITION_TOOL_NAME = 'go_to_position';
export const SET_WINDOW_LEVEL_TOOL_NAME = 'set_window_level';
// Structure colour is saved with the structure set: gated (Apply card).
export const SET_STRUCTURE_COLOR_TOOL_NAME = 'set_structure_color';
// In-place structure edits like the panel/toolbar (Apply card, one undo
// step; refused for approved structures).
export const INTERPOLATE_STRUCTURE_TOOL_NAME = 'interpolate_structure';
export const SMOOTH_STRUCTURE_TOOL_NAME = 'smooth_structure';
export const MOVE_STRUCTURE_TOOL_NAME = 'move_structure';
// Structure management like the structure list and the structure set
// settings (Apply card; most cannot be undone in the viewer).
export const RENAME_STRUCTURE_TOOL_NAME = 'rename_structure';
export const DELETE_STRUCTURE_TOOL_NAME = 'delete_structure';
export const DUPLICATE_STRUCTURE_TOOL_NAME = 'duplicate_structure';
export const REPLACE_STRUCTURE_CONTOURS_TOOL_NAME =
    'replace_structure_contours';
export const CREATE_STRUCTURE_SET_TOOL_NAME = 'create_structure_set';
export const CREATE_MARKER_TOOL_NAME = 'create_marker';
export const SET_MARKER_POSITION_TOOL_NAME = 'set_marker_position';
export const SET_STRUCTURE_TYPE_TOOL_NAME = 'set_structure_type';
export const EDIT_STRUCTURE_SET_INFO_TOOL_NAME = 'edit_structure_set_info';
export const SET_STRUCTURE_SET_APPROVAL_TOOL_NAME =
    'set_structure_set_approval';
// Read-only / view-only (no card): structure details, display resets.
export const GET_STRUCTURE_PROPERTIES_TOOL_NAME = 'get_structure_properties';
export const RESET_DISPLAY_TOOL_NAME = 'reset_display';
// Read-only guide (no card): highlights an existing viewer control so the
// clinician can find it; never clicks or changes anything.
export const SHOW_IN_VIEWER_TOOL_NAME = 'show_in_viewer';
// Comparison with an earlier scan (target structures only): listing scans
// and comparing are reads; building the comparison set is gated.
export const LIST_TIMEPOINTS_TOOL_NAME = 'list_timepoints';
export const BRING_PRIOR_CONTOUR_TOOL_NAME = 'bring_prior_contour';
export const COMPARE_STRUCTURES_TOOL_NAME = 'compare_structures';
// The controls show_in_viewer can point at — only those with a stable
// identifier (data-testid) in the viewer. Here (zod-free leaf) so the
// server enum and the browser's target catalogue share one list.
export const SHOW_IN_VIEWER_TARGETS = [
    // Structure list / selected structure
    'ai-assistant',
    'toggle-all-visibility',
    'new-structure',
    'focus-structure',
    'rename-structure',
    'structure-properties',
    'structure-more-actions',
    'delete-structure',
    'structure-type',
    // Structure set header, settings (gear) and menu
    'structure-set-settings',
    'approval-status',
    'structure-set-label',
    'structure-set-description',
    'approval-chip',
    'structure-set-menu',
    'create-structure-set',
    'copy-structure-set',
    'change-structure-set',
    'delete-structure-set',
    'generate-structure-set',
    // Images
    'image-menu',
    'change-image',
    'unload-image',
    // Contouring toolbar
    'static-brush',
    'adaptive-brush',
    'line-draw',
    'eraser',
    'transform',
    'line-deform',
    'full-screen',
    // Viewport overlay
    'window-level-presets',
    'fusion-colors',
    // Workflow steps
    'step-setup',
    'step-synthetic-imaging',
    'step-registration',
    'step-contouring',
    'step-review',
    'step-dose',
    'step-export',
    // Export, settings, data, review, dose, Contour+
    'export',
    'global-settings',
    'contour-thickness-setting',
    'dose-opacity-setting',
    'layout-width-setting',
    'add-data',
    'load-folder',
    'review-approve-structure',
    'review-reject-structure',
    'review-approve-structure-set',
    'review-reject-structure-set',
    'dose-menu',
    'start-auto-segmentation',
] as const;
export type ShowInViewerTarget = (typeof SHOW_IN_VIEWER_TARGETS)[number];

// The one set of mutating tool names the server phase gate and the client
// ≤1-pending guard both key on — a single source so the two gates cannot
// disagree about which tools mutate.
export const MUTATING_TOOL_NAMES: ReadonlySet<string> = new Set<string>([
    APPLY_SYMMETRIC_MARGIN_TOOL_NAME,
    CREATE_RING_TOOL_NAME,
    APPLY_ASYMMETRIC_MARGIN_TOOL_NAME,
    BOOLEAN_OPERATION_TOOL_NAME,
    FILL_CAVITIES_TOOL_NAME,
    REMOVE_SMALL_PARTS_TOOL_NAME,
    CLEAR_CONTOURS_TOOL_NAME,
    PROPOSE_CONTOUR_MODEL_TOOL_NAME,
    START_AUTO_SEGMENTATION_TOOL_NAME,
    PROPOSE_LOAD_SERIES_TOOL_NAME,
    CANCEL_AUTO_SEGMENTATION_TOOL_NAME,
    SEND_STRUCTURE_SET_TO_PACS_TOOL_NAME,
    SET_STRUCTURE_COLOR_TOOL_NAME,
    INTERPOLATE_STRUCTURE_TOOL_NAME,
    SMOOTH_STRUCTURE_TOOL_NAME,
    MOVE_STRUCTURE_TOOL_NAME,
    RENAME_STRUCTURE_TOOL_NAME,
    DELETE_STRUCTURE_TOOL_NAME,
    DUPLICATE_STRUCTURE_TOOL_NAME,
    REPLACE_STRUCTURE_CONTOURS_TOOL_NAME,
    CREATE_STRUCTURE_SET_TOOL_NAME,
    CREATE_MARKER_TOOL_NAME,
    SET_MARKER_POSITION_TOOL_NAME,
    SET_STRUCTURE_TYPE_TOOL_NAME,
    EDIT_STRUCTURE_SET_INFO_TOOL_NAME,
    SET_STRUCTURE_SET_APPROVAL_TOOL_NAME,
    BRING_PRIOR_CONTOUR_TOOL_NAME,
]);
